import {
  CenterFocusStrong,
  Circle,
  Info,
  LockOpen,
  LockOutlined,
  OpenInFull,
  OpenInNew,
  Public,
  PublicOff,
  StopCircle,
  SwitchVideo,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { getBackButtonSearchParams } from 'components/layouts';
import { UserContext } from 'contexts';
import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoPlayerFunctionality } from 'types';
import useCameraDetailsOverlayStyles from '../styles';
import { useCameraOverlayMutations } from './hooks';
import { CameraControlsProps } from './types';

import {
  KineticModule,
  PrivilegeType,
  RecordingType,
  VideoStreamAccessType,
} from '@kinetic-ui/data-layer';
import { CAMERA_PLAYER_PAGE_ROUTE } from 'const/routes';
import { useAllPrivileges } from 'kineticUtils';

import clsx from 'clsx';
import ScreenLayout from 'components/ScreenLayout';
import { CAMERA_PLAYER_PAGE_HEIGHT, CAMERA_PLAYER_PAGE_WIDTH } from 'const';
import { useTogglePTZLock } from '../useTogglePTZLock';
import TourControlButton from './ControlButtons/TourControlButton';

const DEFAULT_IMPROMTU_RECORDING_NAME = 'short';

const CameraControls: React.FunctionComponent<CameraControlsProps> = (props) => {
  const classes = useCameraDetailsOverlayStyles();
  const { t } = useTranslation(['video', 'common']);
  const { id: userId } = useContext(UserContext);

  const {
    data,
    refetchData,
    videoPlayerFunctionalities,
    controlsButtonRef,
    setShowControls,
    backButtonConfig,
    tour,
    isTourPaused,
    toggleIsTourPaused,
    cameraDetailRoute,
    setShowLockOverrideDialog,
    screenLayoutButtonRef,
    setShowScreenList,
    buttonsWrapperRef,
    presetMenuButtonRef,
    cameraInformationButtonRef,
    setShowCameraInformation,
    handleOpenFullScreen,
    setShowPresets,
    publicButtonRef,
    setShowBlockPopover,
    currentBlock,
  } = props;

  const { togglePTZLock } = useTogglePTZLock(data, refetchData);

  const hasPTZOverrideLockPrivilege = useAllPrivileges(
    [PrivilegeType.PtzOverrideLock],
    KineticModule.Video
  );

  const hasManageRecordingsPrivilege = useAllPrivileges(
    [PrivilegeType.ManageRecordings],
    KineticModule.Video
  );

  const isPublicFeedOff = useMemo(() => {
    const streamManagerId = data.streams?.filter(
      (stream) => stream.accessType === VideoStreamAccessType.Public
    )[0]?.streamManager;

    if (!streamManagerId) {
      return false;
    }

    return !data.publicFeedEnabled;
  }, [data]);

  const { toggleRecording, toggleIsPublic } = useCameraOverlayMutations(data?.id, refetchData);

  const isLockedByCurrentUser = useMemo(
    () => (data?.ptzLock?.userId === userId ? true : false),
    [data?.ptzLock?.userId, userId]
  );

  const backButtonParams = useMemo(() => {
    if (!backButtonConfig) {
      return '';
    }
    return getBackButtonSearchParams(backButtonConfig);
  }, [backButtonConfig]);

  const handleToggleIsPublic = () => {
    const streamManagerId = data.streams?.filter(
      (stream) => stream.accessType === VideoStreamAccessType.Public
    )[0]?.streamManager;
    if (!streamManagerId) {
      console.warn('No stream manager ID found');
      return;
    }
    return toggleIsPublic(data.id, streamManagerId, !data.publicFeedEnabled);
  };

  const handleOpenInNewWindow = () => {
    if (cameraDetailRoute) {
      window.open(cameraDetailRoute + `?${backButtonParams.toString()}`, '_blank');
      return;
    }

    const url = `${CAMERA_PLAYER_PAGE_ROUTE}/${data.id}`;
    const windowName = `${t('cameraPlayerPage')}_${Date.now()}`;
    const features = `width=${CAMERA_PLAYER_PAGE_WIDTH},height=${CAMERA_PLAYER_PAGE_HEIGHT}`;

    window.open(url, windowName, features);
  };

  const recordingInProgress = useMemo(() => {
    if (!data.liveRecordings) {
      return false;
    }
    const manualRecordings = data.liveRecordings.filter(
      (recording) => recording.recordingType === RecordingType.Manual
    );
    return manualRecordings.length > 0;
  }, [data]);

  const handleToggleIsRecording = () => {
    const streamRecorderId = data.streams?.find(
      (stream) => stream.accessType === VideoStreamAccessType.Private
    )?.recordingServer;
    streamRecorderId &&
      toggleRecording(
        data.id,
        streamRecorderId,
        VideoStreamAccessType.Private,
        !recordingInProgress,
        DEFAULT_IMPROMTU_RECORDING_NAME,
        RecordingType.Manual
      );
  };

  const hasCameraControls = useMemo(
    () =>
      videoPlayerFunctionalities.some((v) =>
        [
          VideoPlayerFunctionality.IRIS_CONTROL,
          VideoPlayerFunctionality.CAPABILITIES,
          VideoPlayerFunctionality.FOCUS_CONTROL,
          VideoPlayerFunctionality.DETECTIONS,
        ].includes(v)
      ),
    [videoPlayerFunctionalities]
  );

  const handleToggleIsLocked = useCallback(() => {
    if (isLockedByCurrentUser || !data.ptzLock?.isLocked) {
      togglePTZLock();
      return;
    }
    hasPTZOverrideLockPrivilege && setShowLockOverrideDialog(true);
  }, [
    data.ptzLock?.isLocked,
    hasPTZOverrideLockPrivilege,
    isLockedByCurrentUser,
    setShowLockOverrideDialog,
    togglePTZLock,
  ]);

  const kvdsPublicStream = useMemo(
    () =>
      data.streams?.find(
        (s) => s?.accessType === VideoStreamAccessType.Public && s?.streamType === 'KVDS'
      ),
    [data.streams]
  );

  const isKvdsBlocked = Boolean(currentBlock);
  const isServerConfirmed = Boolean(currentBlock?.serverConfirmed);
  const globeColorClass = isKvdsBlocked
    ? isServerConfirmed
      ? classes.globeBlockedConfirmed
      : classes.globeBlockedPending
    : undefined;
  const defaultTooltipTitle = t(data.publicFeedEnabled ? 'cutPublicFeedOff' : 'publicFeedOn');
  const publicTooltipTitle = isKvdsBlocked
    ? isServerConfirmed
      ? t('publicStreamBlockConfirmed')
      : t('publicStreamBlockPending')
    : undefined;
  const showOffIcon = isPublicFeedOff || isKvdsBlocked;

  const onPublicButtonClick = () => {
    if (kvdsPublicStream) {
      setShowBlockPopover((prev) => !prev);
    } else {
      handleToggleIsPublic();
    }
  };

  return (
    <>
      {tour && videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PAUSE_TOUR) && (
        <TourControlButton
          data={data}
          tour={tour}
          isTourPaused={isTourPaused}
          refetchData={refetchData}
          toggleIsTourPaused={toggleIsTourPaused}
        />
      )}
      {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.RECORDING) &&
        hasManageRecordingsPrivilege && (
          <Tooltip
            placement='top'
            title={t(`video:${recordingInProgress ? 'stopRecording' : 'record'}`)}
            PopperProps={{ container: buttonsWrapperRef.current }}
          >
            <IconButton size='small' onClick={handleToggleIsRecording}>
              {recordingInProgress ? (
                <StopCircle className={classes.recordingButton} fontSize='small' />
              ) : (
                <Circle className={classes.recordingButton} fontSize='small' />
              )}
            </IconButton>
          </Tooltip>
        )}
      {hasCameraControls && (
        <Tooltip
          placement='top'
          title={t('common:cameraControls')}
          PopperProps={{ container: buttonsWrapperRef.current }}
        >
          <IconButton
            ref={controlsButtonRef}
            onClick={() => setShowControls((prev) => !prev)}
            size='small'
          >
            <CenterFocusStrong className={classes.videoControllsButton} fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
      {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PUBLIC_FEED) && (
        <Tooltip
          placement='top'
          title={publicTooltipTitle ?? defaultTooltipTitle}
          PopperProps={{ container: buttonsWrapperRef.current }}
        >
          <IconButton size='small' onClick={onPublicButtonClick} ref={publicButtonRef}>
            {showOffIcon ? (
              <PublicOff
                className={clsx(classes.videoControllsButton, globeColorClass)}
                fontSize='small'
              />
            ) : (
              <Public className={classes.videoControllsButton} fontSize='small' />
            )}
          </IconButton>
        </Tooltip>
      )}
      {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PRESET_CONTROL) && (
        <Tooltip
          placement='top'
          title={t('video:selectPreset')}
          PopperProps={{ container: presetMenuButtonRef.current }}
        >
          <IconButton
            ref={presetMenuButtonRef}
            size='small'
            onClick={() => setShowPresets((prev) => !prev)}
          >
            <SwitchVideo className={classes.videoControllsButton} fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
      {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.CAMERA_LOCK) && (
        <Tooltip
          placement='top'
          title={
            data.ptzLock?.isLocked
              ? t('lockedBy', { fullName: data.ptzLock?.fullName || '' })
              : t('lockPTZ')
          }
          PopperProps={{ container: buttonsWrapperRef.current }}
        >
          <IconButton size='small' onClick={handleToggleIsLocked}>
            {data.ptzLock?.isLocked ? (
              <LockOutlined className={classes.videoControllsButton} fontSize='small' />
            ) : (
              <LockOpen className={classes.videoControllsButton} fontSize='small' />
            )}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        placement='top'
        title={t('cameraInformation')}
        PopperProps={{ container: cameraInformationButtonRef.current }}
      >
        <IconButton
          size='small'
          ref={cameraInformationButtonRef}
          onClick={() => setShowCameraInformation((prev) => !prev)}
        >
          <Info className={classes.videoControllsButton} fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement='top'
        title={t('openInNewWindow')}
        PopperProps={{ container: buttonsWrapperRef.current }}
      >
        <IconButton size='small' onClick={handleOpenInNewWindow}>
          <OpenInNew className={classes.videoControllsButton} fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement='top'
        title={t('common:fullscreen')}
        PopperProps={{ container: buttonsWrapperRef.current }}
      >
        <IconButton size='small' onClick={handleOpenFullScreen}>
          <OpenInFull className={classes.videoControllsButton} fontSize='small' />
        </IconButton>
      </Tooltip>

      {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.SCREEN_LAYOUT_CONTROL) && (
        <Tooltip
          title={t('selectScreen')}
          PopperProps={{ container: screenLayoutButtonRef.current }}
        >
          <IconButton
            ref={screenLayoutButtonRef}
            onClick={() => {
              setShowScreenList((prev) => !prev);
            }}
            size='small'
          >
            <ScreenLayout.ScreenSelectLayout />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default CameraControls;
